import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton,
  Link, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import OnlineAdditionalActChecklist from "./OnlineAdditionalActChecklist";
import useOnlineAdditionalAct from "./useOnlineAdditionalAct";
import { buildApiUrl } from "../../apiConfig";
import { formatRon, parseRonCents, priceReductionErrorMessage, validatePriceReduction } from "./priceReduction";

const ContactLine = ({ icon, label, value, href }) => (
  <Stack spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1, textAlign: "center" }}>
    <Box sx={{ color: "#6a0dad", display: "flex" }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
        {value && href ? <Link href={href} underline="hover">{value}</Link> : value || "Indisponibil"}
      </Typography>
    </Box>
  </Stack>
);

export default function OnlinePriceReductionPanel({ issueId, onClose }) {
  const titleRef = useRef(null);
  const [state, setState] = useState({});
  const [revision, setRevision] = useState(0);
  const [newPrice, setNewPrice] = useState("");
  const [imageFailed, setImageFailed] = useState(false);
  const requestKey = `${issueId}:${revision}`;

  useEffect(() => { setNewPrice(""); }, [issueId]);
  useEffect(() => { titleRef.current?.focus(); }, [issueId]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ key: requestKey, loading: true });
    setImageFailed(false);
    axios.get(buildApiUrl(`/api/tasks/online-price-reduction/${encodeURIComponent(issueId)}`), {
      signal: controller.signal,
    }).then(({ data }) => {
      if (!active) return;
      if (data?.ok !== true || data.issueId !== issueId) {
        throw new Error("Datele produsului nu au putut fi verificate.");
      }
      setState({ key: requestKey, loading: false, data });
    }).catch((error) => {
      if (!active) return;
      setState({
        key: requestKey,
        loading: false,
        error: priceReductionErrorMessage(error, "Nu am putut încărca datele produsului. Reîncearcă."),
      });
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [issueId, requestKey]);

  const isCurrent = state.key === requestKey;
  const loading = !isCurrent || state.loading;
  const data = isCurrent ? state.data : null;
  const error = isCurrent ? state.error : "";
  const additionalAct = useOnlineAdditionalAct({ issueId, data, newPrice });
  const displayedPrice = additionalAct.newPrice;
  const currentPriceAvailable = parseRonCents(data?.currentPrice) > 0;
  const validation = validatePriceReduction(data?.currentPrice, displayedPrice);
  const hasProposal = Boolean(displayedPrice.trim());
  const isEligible = data?.eligibility?.eligible === true;

  return (
    <Box
      component="section"
      id="online-price-panel"
      aria-labelledby="online-price-panel-title"
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", overflowX: "clip" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2 }}>
        <Box>
          <Typography id="online-price-panel-title" ref={titleRef} tabIndex={-1} variant="subtitle1" fontWeight={800} color="#6a0dad">
            Diminuare Preț
          </Typography>
          <Typography variant="body2" color="text.secondary">{issueId}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton
            component="a" href={`https://luxura.atlassian.net/browse/${encodeURIComponent(issueId)}`}
            target="_blank" rel="noopener noreferrer" aria-label="Deschide task în Jira"
            sx={{ bgcolor: "rgba(0,0,0,0.05)", "&:hover": { bgcolor: "rgba(0,0,0,0.1)" } }}
          ><OpenInNewIcon /></IconButton>
          <IconButton onClick={onClose} aria-label="Închide diminuarea prețului"
            sx={{ bgcolor: "rgba(0,0,0,0.05)", "&:hover": { bgcolor: "rgba(0,0,0,0.1)" } }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box>
        {loading && (
          <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress aria-label="Se încarcă produsul" />
            <Typography color="text.secondary">Se încarcă datele actuale ale produsului…</Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" action={<Button color="inherit" onClick={() => setRevision((value) => value + 1)}>Reîncearcă</Button>}>
            {error}
          </Alert>
        )}
        {data && (
          <Stack spacing={2.5}>
            <Box sx={{ minHeight: 180, bgcolor: "#f5f6fa", borderRadius: "10px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {data.image?.url && !imageFailed ? (
                <Box component="img" src={buildApiUrl(data.image.url)} alt={data.summary || "Produs"}
                  onError={() => setImageFailed(true)} sx={{ display: "block", width: "100%", maxHeight: 360, objectFit: "contain" }} />
              ) : (
                <Stack alignItems="center" spacing={1} color="text.secondary">
                  <ImageOutlinedIcon sx={{ fontSize: 48 }} />
                  <Typography variant="body2">Imagine indisponibilă</Typography>
                </Stack>
              )}
            </Box>
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", overflowWrap: "anywhere" }}>{data.summary || issueId}</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip size="small" label={`Contract: ${data.nrContract || "—"}`} />
                <Chip size="small" label={`SKU: ${data.sku || "—"}`} />
                <Chip size="small" label={data.status || "ONLINE"} color="success" variant="outlined" />
              </Stack>
            </Box>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <ContactLine icon={<PersonOutlineIcon />} label="Nume vânzător" value={data.seller?.name} />
              <ContactLine icon={<PhoneOutlinedIcon />} label="Telefon" value={data.seller?.phone} href={data.seller?.phone ? `tel:${data.seller.phone}` : undefined} />
              <ContactLine icon={<MailOutlineIcon />} label="Email" value={data.seller?.email} href={data.seller?.email ? `mailto:${data.seller.email}` : undefined} />
            </Stack>
            <Divider />
            {!isEligible && <Alert severity="warning">{data.eligibility?.reason || "Produsul nu mai este disponibil pentru diminuarea prețului în Online."}</Alert>}
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Suma negociată telefonic</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Introdu noua sumă agreată cu vânzătorul pentru acest produs.
              </Typography>
            </Box>
            <TextField label="Suma actuală" value={formatRon(data.currentPrice)} fullWidth InputProps={{ readOnly: true }} />
            {!currentPriceAvailable && <Alert severity="warning">Suma actuală nu este disponibilă. Diminuarea nu poate fi calculată.</Alert>}
            <TextField
              label="Noua sumă agreată (RON)"
              value={displayedPrice}
              onChange={(event) => { if (!additionalAct.locked) setNewPrice(event.target.value); }}
              fullWidth
              disabled={!currentPriceAvailable || additionalAct.locked}
              inputProps={{ inputMode: "decimal" }}
              error={hasProposal && !validation.isValid}
              helperText={hasProposal && !validation.isValid ? validation.message : "Noua sumă trebuie să fie mai mică decât suma actuală."}
            />
            {validation.isValid && (
              <Alert severity="info">Diminuare: <strong>{formatRon(validation.difference)}</strong></Alert>
            )}
            <OnlineAdditionalActChecklist
              data={data}
              newPrice={displayedPrice}
              onRefresh={additionalAct.locked ? undefined : () => setRevision((value) => value + 1)}
            />
            {additionalAct.feedback && <Alert severity={additionalAct.feedback.severity}>{additionalAct.feedback.message}</Alert>}
            <Box sx={{ borderRadius: 2, p: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <Button fullWidth variant="contained" disabled={!additionalAct.canSubmit} onClick={additionalAct.create}
                startIcon={additionalAct.creating ? <CircularProgress size={18} color="inherit" /> : <DescriptionOutlinedIcon />}>
                {additionalAct.creating ? "Se trimite prin Zoho…" : additionalAct.recoverable
                  ? "Reia trimiterea actului LP015" : "Creează actul adițional LP015"}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Numărul actului este LP015. Data actului se completează automat la creare.
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
